export type GuideCategory =
  | "Getting Started"
  | "Equipment Setup"
  | "Vinyl Care"
  | "Collecting Skills"
  | "Digital & Hybrid"
  | "Advanced";

export type GuideStep = {
  heading: string;
  icon: string;
  content: string[];
};

export type Guide = {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: GuideCategory;
  introduction: string[];
  steps: GuideStep[];
  proTips: string[];
  faq: { question: string; answer: string }[];
};

export const GUIDE_CATEGORIES: GuideCategory[] = [
  "Getting Started",
  "Equipment Setup",
  "Vinyl Care",
  "Collecting Skills",
  "Digital & Hybrid",
  "Advanced",
];

export const guides: Guide[] = [
  // ─── GETTING STARTED ────────────────────────────────
  {
    slug: "how-to-start-vinyl-collection",
    title: "How to Start a Vinyl Collection",
    metaTitle: "How to Start a Vinyl Collection in 2026: Complete Beginner's Guide",
    metaDescription:
      "Everything you need to start collecting vinyl records — from choosing equipment and buying your first records to proper storage and care. A step-by-step guide for complete beginners.",
    category: "Getting Started",
    introduction: [
      "Starting a vinyl collection is one of the most rewarding things you can do as a music lover. There's something irreplaceable about holding a record, placing it on a turntable, and hearing your favorite album fill the room through an analog signal chain. It changes how you listen.",
      "This guide walks you through everything from first equipment purchase to your tenth record. No audiophile gatekeeping, no snobbery — just practical advice to get you spinning records and loving it.",
    ],
    steps: [
      {
        heading: "Choose Your Turntable",
        icon: "🎛️",
        content: [
          "You need a turntable with adjustable tracking force and a replaceable cartridge. Our recommendation for most beginners: the Audio-Technica AT-LP60X ($129) for the simplest setup, or the AT-LP120XUSB ($249) if you want more features and upgrade potential.",
          "Avoid suitcase-style turntables under $60 — they use heavy tracking forces that can damage records over time. Spending a little more on your first turntable protects every record you'll buy.",
        ],
      },
      {
        heading: "Get Speakers",
        icon: "🔊",
        content: [
          "You need powered (active) speakers — these have a built-in amplifier so you just connect them to your turntable with RCA cables. The Edifier R1280T ($99) is the most popular choice for beginners and sounds excellent with vinyl.",
          "If your turntable doesn't have a built-in phono preamp, you'll need speakers with a phono input (like the Kanto YU4) or a separate phono preamp between the turntable and speakers.",
        ],
      },
      {
        heading: "Buy Your First Records",
        icon: "💿",
        content: [
          "Start with albums you already love from streaming — music you know you'll sit down and listen to from start to finish. Use GoOffline to paste your Spotify playlist and get a vinyl shopping list with prices.",
          "Buy 5–10 records to start. Mix new releases (guaranteed quality) with a couple of used records from a local store (affordable, and crate-digging is part of the fun). Don't overthink it — buy what excites you.",
        ],
      },
      {
        heading: "Set Up Your Space",
        icon: "🏠",
        content: [
          "Place your turntable on a stable, level surface away from speakers (speaker vibration can cause feedback). Keep it away from direct sunlight and heat sources. A dedicated shelf or table works perfectly.",
          "Store records vertically, like books — never stacked flat. An IKEA Kallax shelf unit is the vinyl collector's standard: each cube holds about 65 records. Even a simple crate works for your first batch.",
        ],
      },
      {
        heading: "Learn Basic Care",
        icon: "🧹",
        content: [
          "Buy a carbon fiber anti-static brush ($15) and use it before every play — just sweep gently across the record surface to remove dust. This single habit prevents 90% of unnecessary surface noise and stylus wear.",
          "Handle records by the edges and label only — fingerprints leave oils that attract dust and degrade sound. When you've built up 10+ records, invest in MoFi-style anti-static inner sleeves ($25 for 50) to replace the paper sleeves that come stock.",
        ],
      },
      {
        heading: "Play Your First Record",
        icon: "🎵",
        content: [
          "Place the record on the platter, start the motor, gently lower the tonearm to the lead-in groove (or press play if automatic). Sit down, listen to the full side without touching your phone. Let the music fill the room. This is the moment you'll understand why people love vinyl.",
          "When Side A ends, flip the record for Side B. This is the vinyl ritual — the act of flipping creates a natural intermission that's been part of the album experience since the LP format was invented in 1948. Enjoy it.",
        ],
      },
    ],
    proTips: [
      "Use GoOffline to convert your Spotify playlists — your streaming history is your vinyl shopping list",
      "Join r/vinyl on Reddit — the community is welcoming and full of helpful advice",
      "Visit your local record store — staff can recommend pressings and help you avoid overpriced reissues",
      "Don't chase colored vinyl or limited editions for your first records — focus on music you love",
      "Set a monthly vinyl budget ($30–$50) to keep the hobby sustainable and the anticipation alive",
      "Catalog your collection on Discogs from day one — it tracks value and prevents duplicate purchases",
    ],
    faq: [
      {
        question: "How much does it cost to start a vinyl collection?",
        answer:
          "Turntable ($129–$250) + powered speakers ($99–$150) + accessories ($40) + first 5 records ($100–$150) = $370–$590 total to be up and running with a great setup and a small starter collection.",
      },
      {
        question: "Should I buy new or used records?",
        answer:
          "Start with new reissues for guaranteed quality. Once you're comfortable evaluating record condition, used vinyl from reputable sellers and local stores is an amazing way to expand your collection affordably.",
      },
      {
        question: "Is vinyl collecting expensive?",
        answer:
          "It can be as affordable or expensive as you want. Used CDs cost $1–$5. Used vinyl costs $5–$15. New vinyl costs $25–$35. After the initial equipment investment, spending $30–$50/month builds a meaningful collection over time.",
      },
    ],
  },
  {
    slug: "how-to-start-cd-collection",
    title: "How to Start a CD Collection",
    metaTitle: "How to Start a CD Collection in 2026: The Budget-Friendly Way to Own Music",
    metaDescription:
      "CDs are the cheapest way to own music you love. Learn how to start a CD collection, find $1–$5 albums, rip to FLAC, and build your own lossless music library.",
    category: "Getting Started",
    introduction: [
      "CDs are the most underrated format in music right now. For $1–$5 at a thrift store, you get lossless digital audio you own forever — better quality than Spotify, at a fraction of the cost of vinyl. If you want to own music without spending a fortune, CDs are the answer.",
      "This guide shows you how to start a CD collection, where to find incredible deals, and how to rip your CDs to FLAC for a personal lossless streaming library. Physical ownership plus digital convenience — the best of both worlds.",
    ],
    steps: [
      {
        heading: "Find a CD Player (or Don't)",
        icon: "📀",
        content: [
          "You might already have one: check your car, your computer, or an old stereo system. Many laptops still have optical drives, and USB CD drives cost $15–$20. A dedicated CD player for your stereo system costs $50–$200 and sounds great.",
          "Alternatively, you can skip the player entirely — buy CDs purely for ripping to FLAC. A USB CD drive + free ripping software gives you a lossless digital music library that sounds better than streaming, costs less than streaming, and is yours forever.",
        ],
      },
      {
        heading: "Start Hunting for CDs",
        icon: "🔍",
        content: [
          "Thrift stores are the gold mine — Goodwill, Salvation Army, and local charity shops sell CDs for $0.50–$2.00 each. Library sales are even cheaper (sometimes $0.25 each). Estate sales, garage sales, and flea markets are other excellent sources.",
          "Online, Discogs, eBay, and ThriftBooks sell used CDs for $3–$8 shipped. For new CDs, Amazon and your local music store have competitive prices at $8–$15. Either way, CDs are dramatically cheaper than vinyl or streaming long-term.",
        ],
      },
      {
        heading: "Start with Albums You Love",
        icon: "❤️",
        content: [
          "Use GoOffline to paste your Spotify playlist and see which albums are available on CD with prices. Start with your 10 most-played albums — these are the ones that deserve physical ownership first.",
          "Mix it up: buy some albums you know you love, plus a few random finds based on cover art or artist name. Thrift store CDs are cheap enough to take risks — you might discover your new favorite album for $1.",
        ],
      },
      {
        heading: "Rip to FLAC (Optional but Powerful)",
        icon: "💻",
        content: [
          "Ripping CDs to FLAC (Free Lossless Audio Codec) gives you bit-perfect digital copies you can play from any device. Free software: Exact Audio Copy (Windows) or XLD (Mac). Insert CD, click rip, and you'll have lossless files in minutes.",
          "Use MusicBrainz Picard (free) to automatically tag your FLAC files with correct album art, track names, and metadata. Then add them to a music player (foobar2000, Strawberry) or personal streaming server (Jellyfin, Navidrome, Plex).",
        ],
      },
      {
        heading: "Build Your Personal Streaming Server",
        icon: "🌐",
        content: [
          "The ultimate power move: install Navidrome or Jellyfin on a computer, NAS, or Raspberry Pi. Add your ripped FLAC files. Install a mobile app (Symfonium for Android, Finamp for iOS). You now have your own personal Spotify — lossless quality, no monthly fee, no licensing changes, forever.",
          "This setup gives you the convenience of streaming (access anywhere, any device) with the permanence of ownership. Your CDs are the physical archive; the FLAC files serve daily convenience. You own your music infrastructure top to bottom.",
        ],
      },
    ],
    proTips: [
      "Thrift stores restock CD sections every few days — visit regularly for fresh finds",
      "Check the disc condition in-store — surface scratches are usually fine, deep gouges are not",
      "K-pop CDs with photo cards are the exception to bargain pricing — they're collectible and hold value",
      "Rip CDs before shelving them — you'll have a digital backup from day one",
      "Store CDs upright, out of direct sunlight — they last essentially forever with basic care",
      "Use Discogs to catalog your CD collection — it's not just for vinyl",
    ],
    faq: [
      {
        question: "Are CDs really better quality than Spotify?",
        answer:
          "Yes. CDs deliver 16-bit/44.1kHz uncompressed PCM audio (1,411 kbps). Spotify's best quality is 320kbps lossy Ogg Vorbis. Whether you can hear the difference depends on your equipment, but the CD signal is objectively more complete.",
      },
      {
        question: "What's the cheapest way to start?",
        answer:
          "USB CD drive ($15) + thrift store CDs ($1–$2 each) + Exact Audio Copy (free software). For $30–$40 you can have a CD drive and 10+ albums ripped to lossless. That's cheaper than a single month of Spotify for music you own forever.",
      },
      {
        question: "Aren't CDs obsolete?",
        answer:
          "No. CD sales grew in 2023 for the first time in 20 years. Gen Z and K-pop fans are driving a revival. The format offers lossless audio, true ownership, and incredible value. CDs are the comeback story streaming companies don't want you to notice.",
      },
    ],
  },

  // ─── EQUIPMENT SETUP ────────────────────────────────
  {
    slug: "how-to-set-up-turntable",
    title: "How to Set Up a Turntable",
    metaTitle: "How to Set Up a Turntable: Step-by-Step First-Time Setup Guide",
    metaDescription:
      "Complete step-by-step guide to setting up your first turntable. From unboxing to playing your first record — counterweight, anti-skate, phono preamp, and speaker connection.",
    category: "Equipment Setup",
    introduction: [
      "Setting up a turntable properly is essential for great sound and record preservation. A poorly set-up turntable damages vinyl, sounds bad, and gives a false impression of the format. A well set-up turntable reveals music in ways streaming can't match.",
      "This guide covers everything from unboxing to first play. Even if you bought a simple turntable like the AT-LP60X, following these steps ensures you're getting the best possible experience from day one.",
    ],
    steps: [
      {
        heading: "Unbox and Place Your Turntable",
        icon: "📦",
        content: [
          "Remove all packaging materials, including any transit screws or tonearm clips that secure the moving parts during shipping. Check the manual — most turntables have specific packing materials to remove before first use.",
          "Place the turntable on a stable, level surface. Use a small spirit level on the platter to check. Avoid placing it on the same surface as speakers — vibration causes feedback. A dedicated shelf, wall-mounted platform, or heavy table works best.",
        ],
      },
      {
        heading: "Install the Cartridge and Stylus",
        icon: "💎",
        content: [
          "Many entry-level turntables come with the cartridge pre-installed. If not, follow the manual carefully: attach the cartridge to the headshell, connect the four tiny color-coded wires (red=right hot, green=right ground, white=left hot, blue=left ground), and mount the headshell to the tonearm.",
          "If the stylus is packaged separately, gently attach it to the cartridge body. Never touch the stylus tip — it's a microscopic diamond that can be damaged by finger pressure. Handle by the sides of the stylus housing only.",
        ],
      },
      {
        heading: "Set Tracking Force and Anti-Skate",
        icon: "⚖️",
        content: [
          "The tracking force is how hard the stylus presses into the groove. Too heavy damages records; too light causes skipping and distortion. Set it to the cartridge manufacturer's recommended force (usually 1.5–2.5 grams for typical cartridges).",
          "To set tracking force: turn the counterweight until the tonearm floats level (balanced at zero). Then rotate the numbered dial to zero without moving the weight. Finally, rotate the entire counterweight assembly to the recommended tracking force number. Set anti-skate to match the tracking force.",
        ],
      },
      {
        heading: "Connect to Your Audio System",
        icon: "🔌",
        content: [
          "If your turntable has a built-in phono preamp (check for a phono/line switch): set it to LINE and connect RCA cables to any input on your powered speakers or amplifier. If no built-in preamp: connect to a phono preamp first, then from the preamp to your speakers/amp.",
          "Don't forget the ground wire. If your turntable has a separate ground wire (thin wire with a spade connector), attach it to the grounding post on your preamp or receiver. This eliminates the loud hum caused by ground loops.",
        ],
      },
      {
        heading: "Test and Fine-Tune",
        icon: "🎵",
        content: [
          "Place a record on the platter, start the motor (33 RPM for LPs, 45 RPM for singles), and gently lower the tonearm. You should hear music — if it sounds thin or distorted, check your phono preamp connection. If it hums, check the ground wire.",
          "Listen critically to the first few records. Sound should be warm, full, and detailed. If one channel is louder, check cartridge alignment. If there's excessive sibilance (harsh 's' sounds), tracking force may be too light. If bass sounds muddy, the turntable surface may not be level.",
        ],
      },
    ],
    proTips: [
      "Use a digital stylus force gauge ($15–$25) for precise tracking force — the built-in dial is approximate",
      "Recheck tracking force monthly — counterweights can drift slightly",
      "Place a record mat on the platter if your turntable didn't include one — felt or cork mats work well",
      "Keep the dust cover closed during playback to reduce airborne dust settling on the record",
      "Let your turntable warm up for 30 seconds before playing — belt-drive motors stabilize speed after a moment",
    ],
    faq: [
      {
        question: "My records sound thin and tinny — what's wrong?",
        answer:
          "Most likely, your turntable is set to PHONO mode but connected to a LINE input (or vice versa). A phono signal is very quiet and needs amplification by a phono preamp. Double-check the phono/line switch on your turntable and make sure your signal chain is correct.",
      },
      {
        question: "Why do I hear a loud humming noise?",
        answer:
          "Ground loop. Attach the turntable's ground wire to the grounding post on your preamp or receiver. If the turntable doesn't have a separate ground wire, try connecting a wire from a metal chassis screw on the turntable to the ground post.",
      },
      {
        question: "How do I know if my turntable is set up correctly?",
        answer:
          "Play a record you're familiar with. It should sound warm, full, and detailed — vocals clear, bass present, no harshness or distortion. If something sounds off, the most common fixes are: correct tracking force, proper phono/line switch position, and grounding.",
      },
    ],
  },

  // ─── VINYL CARE ─────────────────────────────────────
  {
    slug: "how-to-clean-vinyl-records",
    title: "How to Clean Vinyl Records",
    metaTitle: "How to Clean Vinyl Records: The Complete Cleaning Guide for 2026",
    metaDescription:
      "Learn how to clean vinyl records properly — from quick dry brushing before every play to deep wet cleaning for used records. Step-by-step methods that actually work.",
    category: "Vinyl Care",
    introduction: [
      "Clean records sound dramatically better. Dust, fingerprints, mold release compound, and accumulated grime all create surface noise — pops, clicks, and hiss that obscure the music underneath. A thorough cleaning can transform a noisy used record into something that sounds nearly new.",
      "This guide covers every cleaning method from the 10-second pre-play brush to deep wet cleaning. Start with the basics and upgrade your routine as your collection grows.",
    ],
    steps: [
      {
        heading: "Dry Brush Before Every Play",
        icon: "🧹",
        content: [
          "This is the most important habit in vinyl care. Before every play, gently place a carbon fiber anti-static brush on the spinning record and let it sweep from the outer edge toward the center. This removes surface dust and discharges static electricity that attracts particles.",
          "Hold the brush lightly — the carbon fibers do the work, not pressure. Sweep for 2–3 rotations, then lift the brush away toward you (pulling collected dust off the record). The AudioQuest Anti-Static Record Brush ($15) is the standard. Every vinyl listener needs one.",
        ],
      },
      {
        heading: "Clean the Stylus Regularly",
        icon: "💎",
        content: [
          "A dirty stylus drags debris through your record grooves, causing damage and degrading sound. Use a dedicated stylus brush — gently brush from back to front (never side to side, which can bend the cantilever). Do this before every listening session.",
          "For deeper stylus cleaning, a small dab of Blu-Tack or a dedicated stylus cleaning gel works well. Gently lower the stylus into the tacky surface and lift. This removes caked-on debris that brushing alone can't reach.",
        ],
      },
      {
        heading: "Wet Clean Used Records",
        icon: "💧",
        content: [
          "Every used record should be wet-cleaned once. The Spin-Clean Record Washer ($80) is the gold standard for home use: fill the basin with distilled water and cleaning solution, rotate the record through the velvet brushes, dry with included lint-free cloths.",
          "If you don't have a Spin-Clean, a spray-and-wipe approach works: spray dedicated cleaning solution (never household cleaners!) onto the record surface, gently wipe with a microfiber cloth in circular motions following the groove direction, then dry with a clean cloth.",
        ],
      },
      {
        heading: "Deep Clean New Records",
        icon: "✨",
        content: [
          "Yes, even new records benefit from cleaning. Manufacturing leaves mold release compound in the grooves — a residue from the pressing process that creates a film over the music signal. A single wet clean removes this and opens up the sound noticeably.",
          "You don't need to deep-clean every new record, but do it for audiophile pressings and any record that sounds duller or noisier than expected on first play. The improvement can be significant.",
        ],
      },
      {
        heading: "Dry and Store Properly",
        icon: "📦",
        content: [
          "After wet cleaning, let records air dry in a clean rack or stand them vertically for 10–15 minutes. Never play a wet record — moisture in the groove damages both the vinyl and your stylus.",
          "Once dry, place the record in an anti-static inner sleeve (MoFi-style poly-lined) — not the stock paper sleeve. Paper sleeves scratch records over time. Store the jacket in a polypropylene outer sleeve to protect the artwork. These small investments protect your entire collection.",
        ],
      },
    ],
    proTips: [
      "Always use distilled water — tap water leaves mineral residue in grooves",
      "Never use rubbing alcohol, Windex, or dish soap — they can damage vinyl",
      "Clean in the direction of the grooves (circular), never across them",
      "If a record still sounds noisy after cleaning, the issue may be stylus wear or groove damage",
      "Store cleaning solution away from sunlight to prevent degradation",
      "For valuable records ($50+), consider professional ultrasonic cleaning at a local record store",
    ],
    faq: [
      {
        question: "How often should I clean my records?",
        answer:
          "Dry brush before every play (10 seconds). Wet clean used records once when you acquire them, and new records once before first play. After that, dry brushing maintains them. Wet clean again if a record starts sounding noisier than usual.",
      },
      {
        question: "Can cleaning fix a scratched record?",
        answer:
          "Cleaning can't repair physical groove damage (scratches). But many records that seem scratched are actually just dirty — embedded dust and grime creates noise that disappears after a proper wet clean. Always try cleaning before writing off a noisy record.",
      },
      {
        question: "Is the Spin-Clean worth $80?",
        answer:
          "If you buy used records, absolutely. A single session can dramatically improve 10–20 records. Many collectors consider the Spin-Clean the best value investment in vinyl care after a basic brush.",
      },
    ],
  },

  // ─── COLLECTING SKILLS ──────────────────────────────
  {
    slug: "how-to-use-discogs",
    title: "How to Use Discogs",
    metaTitle: "How to Use Discogs: The Complete Guide for Record Collectors",
    metaDescription:
      "Master Discogs — the world's largest music database and marketplace. Learn to identify pressings, buy records safely, catalog your collection, and track values.",
    category: "Collecting Skills",
    introduction: [
      "Discogs is the single most important tool for any vinyl or CD collector. It's simultaneously a comprehensive music database (16 million+ releases cataloged), a global marketplace (8 million+ items for sale), and a collection management tool. Learning to use it effectively saves you money, prevents buying the wrong pressing, and helps you build a collection you're proud of.",
      "This guide covers everything from creating your first account to advanced buying techniques that experienced collectors use daily.",
    ],
    steps: [
      {
        heading: "Create Your Account and Profile",
        icon: "👤",
        content: [
          "Sign up at discogs.com (free). Fill in your profile — sellers are more willing to work with buyers who have complete profiles. Enable email notifications for your wantlist (we'll set that up later) and marketplace activity.",
          "Explore the interface: the Search bar is your primary tool, the Marketplace tab is where you buy, and the Collection tab is where you'll catalog your records.",
        ],
      },
      {
        heading: "Learn to Identify Pressings",
        icon: "🔍",
        content: [
          "This is where Discogs becomes invaluable. Search for any album and you'll see multiple 'versions' — different pressings from different countries, years, and labels. Each has unique matrix numbers, catalog numbers, and barcode information.",
          "To identify YOUR specific pressing: check the label (design, catalog number, logo), the dead wax (etched numbers near the center label), and the barcode on the cover. Cross-reference these with Discogs entries to find your exact pressing. This matters for condition grading, value assessment, and knowing whether you have a first pressing or a reissue.",
        ],
      },
      {
        heading: "Buy Records Safely",
        icon: "🛒",
        content: [
          "Before buying: check the seller's rating (99%+ is ideal, avoid below 97%). Read their seller description for grading philosophy and shipping methods. Check their location for shipping cost estimates.",
          "Read condition notes carefully. Discogs uses the Goldmine grading standard (M, NM, VG+, VG, G+, G, F, P). Always check both Media (disc) and Sleeve (cover) grades. A VG+ record has minor surface noise; a VG record has noticeable noise. For records over $20, message the seller to ask about specific issues if the notes are vague.",
        ],
      },
      {
        heading: "Build Your Collection and Wantlist",
        icon: "📚",
        content: [
          "Add every record you own to your Discogs collection. Specify the exact pressing — this tracks your collection's value and helps you avoid buying duplicates. Discogs automatically calculates your collection's median and maximum market values based on recent sales data.",
          "Add records you're searching for to your Wantlist. You'll receive email notifications when items matching your wantlist become available at prices within your configured range. This is the most effective way to find specific pressings without constant manual searching.",
        ],
      },
      {
        heading: "Use Market Data to Buy Smart",
        icon: "📊",
        content: [
          "Every release page shows price statistics: last sold, lowest, median, and highest prices. These are based on actual marketplace sales, not listing prices. Always check these before buying — if a seller's price is significantly above the median, it's overpriced unless the condition justifies the premium.",
          "Track price trends over time. Some records appreciate (limited editions, sought-after pressings), while others depreciate (oversupplied reissues). This data helps you make informed purchasing decisions and identify undervalued records.",
        ],
      },
    ],
    proTips: [
      "Set your wantlist notifications to 'daily digest' to avoid notification overload",
      "Use the 'Explore' feature to discover music similar to what you already collect",
      "Export your collection periodically as a CSV backup",
      "Check 'Items I Want' tab before visiting record stores to know your priorities",
      "Rate sellers after every transaction — it helps the community",
      "Use the Discogs app on your phone when crate-digging to check prices in real-time",
    ],
    faq: [
      {
        question: "Is Discogs free?",
        answer:
          "Yes. Browsing, buying, cataloging your collection, and the wantlist are all free. Sellers pay a small fee on sales (8% of item price + $0.15 per order). There are no buyer fees.",
      },
      {
        question: "How reliable is Discogs marketplace?",
        answer:
          "Very reliable for sellers with high ratings (98%+). Discogs has buyer protection, and you can open disputes for items that arrive in worse condition than described. Stick to established sellers and you'll rarely have issues.",
      },
      {
        question: "Can I sell records on Discogs?",
        answer:
          "Yes. You can list items from your collection for sale. You set the price, grade the condition, and describe any notes. When an item sells, Discogs takes an 8% fee. Shipping is handled between you and the buyer.",
      },
    ],
  },

  // ─── DIGITAL & HYBRID ──────────────────────────────
  {
    slug: "how-to-rip-cds-to-flac",
    title: "How to Rip CDs to FLAC",
    metaTitle: "How to Rip CDs to FLAC: Lossless Digital Copies of Your CD Collection",
    metaDescription:
      "Step-by-step guide to ripping CDs to FLAC files — bit-perfect lossless copies you can play anywhere. Free software, proper settings, and metadata tagging.",
    category: "Digital & Hybrid",
    introduction: [
      "Ripping CDs to FLAC gives you the best of both worlds: physical ownership of the disc plus lossless digital copies you can play from any device. FLAC (Free Lossless Audio Codec) preserves 100% of the CD's audio data while compressing file sizes by 40–60%.",
      "A single ripped CD takes about 300–400 MB in FLAC format. Even a modest 500-CD collection fits comfortably on a 256 GB drive. This guide walks you through the setup, ripping process, and metadata tagging to build a perfect digital library.",
    ],
    steps: [
      {
        heading: "Get a CD Drive and Software",
        icon: "💻",
        content: [
          "If your computer doesn't have a built-in CD drive, buy a USB external drive ($15–$25). Any brand works for ripping — you don't need an expensive audiophile drive.",
          "Software: On Windows, use Exact Audio Copy (EAC) — it's free and produces verified bit-perfect rips. On Mac, use XLD (X Lossless Decoder) — also free and equally accurate. Both tools compare your rip against the AccurateRip database to verify the copy is bit-perfect.",
        ],
      },
      {
        heading: "Configure Your Ripping Software",
        icon: "⚙️",
        content: [
          "In EAC: Run the configuration wizard on first launch. Set output format to FLAC. Enable AccurateRip verification. Set error correction to 'Secure' mode. Choose an output folder structure (we recommend: Artist/Album/Track Number - Track Title.flac).",
          "In XLD: Open Preferences → General. Set output format to FLAC. Enable AccurateRip. Set read mode to 'Paranoid' for maximum accuracy. Configure output filename template to match your preferred folder structure.",
        ],
      },
      {
        heading: "Rip Your CDs",
        icon: "📀",
        content: [
          "Insert a CD. The software should automatically detect it and fetch track names from online databases (CDDB/MusicBrainz). Verify the metadata is correct — sometimes it pulls wrong info for compilations or rare releases.",
          "Click 'Rip' or 'Extract' and wait. A typical CD takes 5–15 minutes depending on your drive speed and error correction settings. You'll get a folder with individual FLAC files for each track. Check the rip log — green/accurate results mean a perfect copy.",
        ],
      },
      {
        heading: "Tag Metadata with MusicBrainz Picard",
        icon: "🏷️",
        content: [
          "Even if your ripping software got the basic tags right, MusicBrainz Picard (free, cross-platform) adds comprehensive metadata: correct album art, full credits, release year, genre, and standardized formatting. It uses acoustic fingerprinting to match files to the correct release.",
          "Open Picard, drag your ripped folders in, let it match each album, review the results, and save. This ensures your digital library has consistent, accurate metadata that music players and servers display correctly.",
        ],
      },
      {
        heading: "Organize and Back Up",
        icon: "📂",
        content: [
          "Store your FLAC library in a consistent folder structure: Music/Artist Name/Album Name (Year)/01 - Track Title.flac. This works universally with all music players and servers.",
          "Back up your library to at least one additional location: an external hard drive, NAS, or cloud storage. Your CDs serve as the physical archive, but re-ripping hundreds of CDs is tedious. A backup drive costs $50–$80 and saves hours of potential re-work.",
        ],
      },
    ],
    proTips: [
      "Rip in 'Secure' or 'Paranoid' mode — it's slower but guarantees accuracy",
      "Always verify against AccurateRip — this confirms your rip matches other verified copies",
      "FLAC compression level doesn't affect audio quality — only file size and decode speed",
      "Keep rip logs — they prove the accuracy of your copies if you ever need to verify",
      "Rip CDs before shelving them — build your digital library from day one",
      "Consider running a personal music server (Navidrome, Jellyfin) for streaming your FLAC library from any device",
    ],
    faq: [
      {
        question: "Is FLAC really lossless?",
        answer:
          "Yes. FLAC is mathematically lossless — the decoded audio is bit-for-bit identical to the original CD data. It compresses file sizes by 40–60% using techniques similar to ZIP, but unlike MP3, no audio data is discarded. FLAC decoded = original CD audio.",
      },
      {
        question: "Why FLAC instead of MP3?",
        answer:
          "MP3 is lossy — it permanently removes audio data to shrink files. A 320kbps MP3 is good but not identical to the CD. FLAC preserves everything. Storage is cheap enough now that there's no reason to use lossy formats for a permanent library.",
      },
      {
        question: "Can I play FLAC on my phone?",
        answer:
          "Yes. Android supports FLAC natively. On iPhone, apps like VLC, Foobar2000, and Plexamp play FLAC files. If you run a personal music server (Navidrome, Jellyfin, Plex), mobile apps stream your FLAC library from anywhere.",
      },
    ],
  },

  // ─── ADVANCED ───────────────────────────────────────
  {
    slug: "how-to-identify-vinyl-pressing",
    title: "How to Identify a Vinyl Pressing",
    metaTitle: "How to Identify a Vinyl Pressing: Matrix Numbers, Labels & Dead Wax Guide",
    metaDescription:
      "Learn to identify which pressing of a vinyl record you have — matrix numbers, dead wax codes, label variations, and Discogs cross-referencing. Essential collector skill.",
    category: "Advanced",
    introduction: [
      "The same album can exist in dozens or even hundreds of different pressings — each with potentially different mastering, pressing quality, and collector value. A first pressing of a Beatles album might be worth $500; a 2020 reissue of the same album costs $30. Knowing what you have is essential for collecting.",
      "Identifying pressings is a learnable skill. It requires checking three things: the dead wax (etched codes near the center label), the label design, and the catalog number. This guide teaches you how to read these identifiers and cross-reference them with Discogs.",
    ],
    steps: [
      {
        heading: "Read the Dead Wax",
        icon: "🔍",
        content: [
          "The dead wax (also called run-out groove) is the blank area between the last track and the label. Hold the record at an angle under bright light — you'll see etched or stamped codes. These are matrix numbers that identify the specific master lacquer and stamper used to press your record.",
          "Write down everything you see: numbers, letters, symbols, and any hand-etched inscriptions. Common elements include the catalog number (e.g., 'PCS 7027'), mastering engineer initials (e.g., 'KG' for Kevin Gray), pressing plant codes, and sometimes mother/stamper identifiers.",
        ],
      },
      {
        heading: "Check the Label",
        icon: "🏷️",
        content: [
          "Record labels changed their designs over time. For example, early UK Parlophone labels (Beatles era) used a yellow-and-black design that changed to a more modern silver-and-black design later. The specific label design narrows down the pressing period significantly.",
          "Note the catalog number on the label, the country of manufacture, any 'Made in...' text, and the label's color scheme and logo design. Take photos for reference when cross-checking on Discogs.",
        ],
      },
      {
        heading: "Cross-Reference on Discogs",
        icon: "💻",
        content: [
          "Search the album on Discogs and click 'All Versions' to see every known pressing. Filter by country and format. Click into specific pressings and compare their listed matrix numbers, catalog numbers, and label descriptions with what you found on your record.",
          "Discogs submissions include photos of labels and dead wax uploaded by the community. Compare your record's label design and dead wax codes with the photos. When everything matches — matrix numbers, catalog number, label design — you've identified your pressing.",
        ],
      },
      {
        heading: "Understand What Your Pressing Means",
        icon: "📊",
        content: [
          "Once identified, check the pressing's sales history on Discogs for market value. Read user reviews for sound quality assessments. Check Steve Hoffman Forums for audiophile opinions on that specific pressing.",
          "Key factors that affect value: first pressings are generally more valuable, specific mastering engineers (Kevin Gray, Bernie Grundman) add value, pressing plant quality matters, and condition is always king — a NM copy of a common pressing can be worth more than a VG copy of a rare one.",
        ],
      },
    ],
    proTips: [
      "Use a bright flashlight or phone torch to read dead wax inscriptions — they can be subtle",
      "Photograph your dead wax and labels — easier to compare digitally than squinting at the record",
      "Some mastering engineers leave signature marks: 'A Porky Prime Cut' (George Peckham), 'KG' (Kevin Gray), 'RL' (Robert Ludwig)",
      "Japanese pressings are often identifiable by their OBI strip and unique catalog numbers starting with manufacturer prefixes",
      "If your matrix numbers include 'DMM' — that's a Direct Metal Mastering pressing, a different (and sometimes preferred) mastering process",
      "When in doubt, post photos of your dead wax and label on the Discogs forums — the community is incredibly helpful at identifying pressings",
    ],
    faq: [
      {
        question: "Why does it matter which pressing I have?",
        answer:
          "Different pressings can sound dramatically different — different mastering, different pressing plants, different source material. They also vary significantly in value. Knowing your pressing helps you understand what you own, assess its worth, and make informed decisions about upgrades.",
      },
      {
        question: "What if I can't find my pressing on Discogs?",
        answer:
          "If your exact matrix numbers and label design don't match any existing entry, you may have an unlisted pressing. You can submit a new entry to Discogs (it's community-maintained). The forums and submission guidelines help ensure accuracy.",
      },
    ],
  },
];

/** Lookup map for O(1) guide access by slug */
export const guideBySlug = new Map(guides.map((g) => [g.slug, g]));

/** Get guides by category */
export const getGuidesByCategory = (category: GuideCategory): Guide[] =>
  guides.filter((g) => g.category === category);

/** Get all guide slugs (for sitemap) */
export const getAllGuideSlugs = (): string[] => guides.map((g) => g.slug);
